import DatePicker from "@/components/date-picker";
import { InputField } from "@/components/input-field/InputField";
import { CustomPopover } from "@/components/popover/CustomPopover";
import { SelectComponent } from "@/components/select/Select";
import { EntityTypes } from "@/constants/enums";
import {
  EntityStatusOption,
  getStatusByEntity,
} from "@/services/entity-status.service";
import { AppColors } from "@/styles/colors";
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import { Filter } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Control, FieldValues, UseFormHandleSubmit } from "react-hook-form";

interface Props {
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  openFilters: boolean;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => void;
  clearFilters: () => void;
  control: Control<FieldValues>;
  disableFilters?: boolean;
  loadingFilters?: boolean;
  filtersApplied?: boolean;
  disableClear?: boolean;
  disableApply?: boolean;
}

export const PurchaseOrderIntentFilter = ({
  openFilters,
  disableFilters,
  loadingFilters,
  filtersApplied,
  disableClear,
  control,
  disableApply,
  submitFilters,
  handleSubmit,
  setOpenFilters,
  clearFilters,
}: Props) => {

  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const [statusOptions, setStatusOptions] = useState<EntityStatusOption[]>([]);
  const [loadingStatusOptions, setLoadingStatusOptions] =
    useState<boolean>(false);

  const fetchStatusOptions = async () => {
    setLoadingStatusOptions(true);
    const response = await getStatusByEntity({
      Id: [EntityTypes.IntencionCompra],
    });

    setStatusOptions(response ?? []);
    setLoadingStatusOptions(false);
  };

  useEffect(() => {
    if (isActive) {
      fetchStatusOptions();
    }
  }, [isActive]);

  return (
    <Flex width="100%" paddingY="10px" justifyContent="flex-end">
      <HStack gap="10">
        <CustomPopover
          open={openFilters}
          onOpenChange={(e) => setOpenFilters(e.open)}
          triggerComponent={
            <Box position="relative" display="inline-block">
              <Button
                variant="outline"
                paddingX="12px"
                height="32px"
                disabled={disableFilters || loadingFilters}
              >
                <Filter size={16} />
                <Text fontWeight={600} fontSize="14px">
                  Filtrar
                </Text>
              </Button>
              {filtersApplied && (
                <Badge
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  borderRadius="full"
                  px="2"
                  py="0.5"
                >
                  !
                </Badge>
              )}
            </Box>
          }
          bodyComponent={
            <Flex
              as="form"
              flexDir="column"
              padding="25px"
              gap="15px"
              onSubmit={handleSubmit(submitFilters)}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <Text fontWeight={600} fontSize="20px">
                  Filtrar
                </Text>

                <Button
                  variant="plain"
                  color={AppColors.BLUE_TEXT}
                  fontWeight={600}
                  onClick={clearFilters}
                  disabled={loadingFilters || disableClear || disableFilters}
                  loading={loadingFilters}
                >
                  Limpiar filtros
                </Button>
              </Flex>

              <Grid templateColumns="repeat(2, 1fr)" gap={4} alignItems="end">
                <GridItem>
                  <InputField
                    inputName="cuenta"
                    labelText="Cuenta"
                    placeholder="Ingresar cuenta"
                    control={control}
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>

                <GridItem>
                  <DatePicker
                    control={control}
                    labelText="Fecha solicitud"
                    name="fechaSolicitud"
                    placeholder="Ingresar fecha"
                  />
                </GridItem>

                <GridItem>
                  <InputField
                    inputName="zonaComercial"
                    labelText="Zona comercial"
                    placeholder="Ingresar zona comercial"
                    control={control}
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>

                <GridItem>
                  <SelectComponent
                    name="tipoInsumo"
                    items={[
                      { label: "Fertilizante", value: 1 },
                      { label: "Combustible", value: 2 },
                    ]}
                    labelText="Tipo de insumo"
                    placeholder="Seleccionar"
                    control={control}
                    minHeight="40px"
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <SelectComponent
                    name="estados"
                    items={statusOptions.map((status) => ({
                      label: status.nombre,
                      value:
                        typeof status.ids?.[0] === "number"
                          ? String(status.ids?.[0])
                          : "",
                    }))}
                    labelText="Estado"
                    placeholder="Seleccionar"
                    control={control}
                    fullWidth
                    loading={loadingStatusOptions}
                    disabled={
                      disableFilters || loadingFilters || loadingStatusOptions
                    }
                    multiple
                    menuMaxHeight="250px"
                  />
                </GridItem>
              </Grid>

              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                type="submit"
                loading={loadingFilters}
                disabled={
                  loadingFilters || disableFilters || loadingStatusOptions || disableApply
                }
              >
                Aplicar filtros
              </Button>
            </Flex>
          }
        />
      </HStack>
    </Flex>
  );
};
